import type { Locale } from "@/i18n/locales";

import {
  landingHero as enHero,
  landingClosing as enClosing,
  landingMeta as enMeta,
} from "./en/landing-hero";
import {
  landingHero as deHero,
  landingClosing as deClosing,
  landingMeta as deMeta,
} from "./de/landing-hero";
import { landingWhy as enWhy, landingInfra as enInfra } from "./en/landing-why";
import { landingWhy as deWhy, landingInfra as deInfra } from "./de/landing-why";
import {
  landingCompare as enCompare,
  landingConfigurator as enConfigurator,
} from "./en/landing-offer";
import {
  landingCompare as deCompare,
  landingConfigurator as deConfigurator,
} from "./de/landing-offer";
import { landingSteps as enSteps, landingAssets as enAssets } from "./en/landing-steps";
import { landingSteps as deSteps, landingAssets as deAssets } from "./de/landing-steps";
import type {
  LandingClosingContent,
  LandingHeroContent,
  LandingCompareContent,
  LandingConfiguratorContent,
  LandingInfraContent,
  LandingMetaContent,
  LandingStepsContent,
  LandingAssetsContent,
  LandingWhyContent,
} from "./types";

// Same completeness mechanism as the chrome registry: a locale added to LOCALES
// fails to compile here until its module exists carrying every field.
const HERO: Readonly<Record<Locale, LandingHeroContent>> = { en: enHero, de: deHero };
const CLOSING: Readonly<Record<Locale, LandingClosingContent>> = {
  en: enClosing,
  de: deClosing,
};

export function landingHeroFor(locale: Locale): LandingHeroContent {
  return HERO[locale];
}

export function landingClosingFor(locale: Locale): LandingClosingContent {
  return CLOSING[locale];
}

const WHY: Readonly<Record<Locale, LandingWhyContent>> = { en: enWhy, de: deWhy };
const INFRA: Readonly<Record<Locale, LandingInfraContent>> = { en: enInfra, de: deInfra };

export function landingWhyFor(locale: Locale): LandingWhyContent {
  return WHY[locale];
}

export function landingInfraFor(locale: Locale): LandingInfraContent {
  return INFRA[locale];
}

const COMPARE: Readonly<Record<Locale, LandingCompareContent>> = {
  en: enCompare,
  de: deCompare,
};
const CONFIGURATOR: Readonly<Record<Locale, LandingConfiguratorContent>> = {
  en: enConfigurator,
  de: deConfigurator,
};

export function landingCompareFor(locale: Locale): LandingCompareContent {
  return COMPARE[locale];
}

export function landingConfiguratorFor(locale: Locale): LandingConfiguratorContent {
  return CONFIGURATOR[locale];
}

/**
 * The evaluation ticket's row labels and worded values, shared by the hero
 * preview and the configurator. They describe the SAME terms, so they resolve to
 * one source — restating them per surface is how "ONE-TIME FEE" and its German
 * counterpart drift into two spellings that only one translator ever updates.
 */
export function evaluationTermsFor(locale: Locale): LandingHeroContent["ticket"] {
  return HERO[locale].ticket;
}

const STEPS_C: Readonly<Record<Locale, LandingStepsContent>> = { en: enSteps, de: deSteps };
const ASSETS_C: Readonly<Record<Locale, LandingAssetsContent>> = { en: enAssets, de: deAssets };

export function landingStepsFor(locale: Locale): LandingStepsContent {
  return STEPS_C[locale];
}

export function landingAssetsFor(locale: Locale): LandingAssetsContent {
  return ASSETS_C[locale];
}

const META: Readonly<Record<Locale, LandingMetaContent>> = { en: enMeta, de: deMeta };

export function landingMetaFor(locale: Locale): LandingMetaContent {
  return META[locale];
}
